import React, { PropsWithChildren, useEffect } from "react";
import { Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./AgeRestrictionModal.scss";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import Rated18Icon from "../icons/Rated18Icon";
import PrimaryActionButton from "../Buttons/PrimaryActionButton/PrimaryActionButton";
import CheckmarkIcon from "../icons/CheckmarkIcon";
import SolidMinusIcon from "../icons/SolidMinusIcon";
import Gem from "../Gem/Gem";
import HyperCoin from "../HyperCoin/HyperCoin";
import { showErrorToastDanger } from "../../utils/show-toast";
import { calculateAge } from "../../utils/helpers";
import { GenericModalProps } from "../../utils/constants/generic-types";
import useAuth from "../../hooks/useAuth";

interface AgeRestrictionModalProps extends GenericModalProps {
  toastMessage?: string;
}

enum RestrictionCategories {
  CAN_DO = "can-do",
  CANNOT_DO = "cannot-do",
}

interface RestrictionItemProps {
  restrictionCategory: RestrictionCategories;
}

interface RestrictionSectionProps {
  title: string;
  restrictionCategory: RestrictionCategories;
}

function RestrictionItem({
  restrictionCategory,
  children,
}: PropsWithChildren<RestrictionItemProps>) {
  return (
    <Stack as="li" gap={2} direction="horizontal" className="RestrictionItem">
      <div
        className={`IconContainer ${
          restrictionCategory === RestrictionCategories.CAN_DO
            ? "CanDo"
            : "CannotDo"
        }`}
      >
        {restrictionCategory === RestrictionCategories.CAN_DO ? (
          <CheckmarkIcon />
        ) : (
          <SolidMinusIcon width={12} height={12} />
        )}
      </div>
      <p className="Grotesk Content">{children}</p>
    </Stack>
  );
}

function RestrictionSection({
  title,
  restrictionCategory,
  children,
}: PropsWithChildren<RestrictionSectionProps>) {
  return (
    <Stack className="RestrictionSection">
      <h2 className="Grotesk Title">{title}</h2>
      <Stack
        as="ul"
        gap={3}
        className={`ListItems ${
          restrictionCategory === RestrictionCategories.CAN_DO
            ? "CanDo"
            : "CannotDo"
        }`}
      >
        {children}
      </Stack>
    </Stack>
  );
}

export default function AgeRestrictionModal({
  isOpen,
  onClose,
  toastMessage,
}: AgeRestrictionModalProps) {
  const { userProfile } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen && toastMessage) {
      showErrorToastDanger(toastMessage);
    }
  }, [isOpen, toastMessage]);

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      showCloseBtn={false}
      containerClass="AgeRestrictionModal"
    >
      <Stack gap={4}>
        <Stack className="align-items-center Header">
          <Rated18Icon />
          <h1 className="text-center Hanson-700 MainCopy">
            {t("age_restriction")}
          </h1>
          <p className="text-center Grotesk SubCopy">
            {t("we_noticed_from_your_kyc_verification_key")} (
            {userProfile?.dob ? calculateAge(userProfile.dob) : ""}){" "}
            {t("is_not_legally_allowed_to_play_games_on_hyper")}.
          </p>
        </Stack>
        <Stack className="CansAndCannots">
          <RestrictionSection
            title={t("what_you_cannot_do")}
            restrictionCategory={RestrictionCategories.CANNOT_DO}
          >
            <RestrictionItem
              restrictionCategory={RestrictionCategories.CANNOT_DO}
            >
              {t("play_battle_games")}
            </RestrictionItem>
            <RestrictionItem
              restrictionCategory={RestrictionCategories.CANNOT_DO}
            >
              {t("top_up_your_hpr_balance")}
            </RestrictionItem>
            <RestrictionItem
              restrictionCategory={RestrictionCategories.CANNOT_DO}
            >
              {t("compete_in_multiplayer_tournament")}
            </RestrictionItem>
          </RestrictionSection>

          <RestrictionSection
            title={t("what_you_can_still_do")}
            restrictionCategory={RestrictionCategories.CAN_DO}
          >
            <RestrictionItem restrictionCategory={RestrictionCategories.CAN_DO}>
              {t("play_practice_games")}
            </RestrictionItem>
            <RestrictionItem restrictionCategory={RestrictionCategories.CAN_DO}>
              {t("play_challenge_games_that_have_zero")}
              <span className="d-inline-flex justify-content-center align-items-center CurrencyWithText">
                <HyperCoin width={13.3} height={13.3} />
                {t("hpr")}
              </span>{" "}
              {t("stake")}
            </RestrictionItem>
            <RestrictionItem restrictionCategory={RestrictionCategories.CAN_DO}>
              {t("cash_out_available_balance")}
            </RestrictionItem>
            <RestrictionItem restrictionCategory={RestrictionCategories.CAN_DO}>
              {t("convert")}{" "}
              <span className="d-inline-flex justify-content-center align-items-center CurrencyWithText">
                <Gem width={16} height={16} />
                {t("gem")}
              </span>{" "}
              {t("to")}{" "}
              <span className="d-inline-flex justify-content-center align-items-center CurrencyWithText">
                <HyperCoin width={13.3} height={13.3} />
                {t("hpr")}
              </span>
            </RestrictionItem>
          </RestrictionSection>
        </Stack>

        <PrimaryActionButton text={t("got_it")} onClick={onClose} />
      </Stack>
    </BottomDrawer>
  );
}
